import { useCallback, useEffect, useRef, useState } from 'react';
import { subscriptionGateway } from '../index';
import { notifySubscriptionChanged } from '../subscriptionChanged';
import type { PlanChangeFeatureImpact, PlanChangePreview } from '../billingTypes';
import { FeatureAvailabilityBadge } from '../../../shared/FeatureAvailabilityBadge';
import type { CompanySubscriptionResponse, PlanSummary } from '../types';
import { ApiError } from '../../identity/types';
import { RetryableError } from '../../../shared/RetryableError';
import { useBilling } from './BillingContext';
import { billingErrorMessage, isPermissionDenied } from './billingErrors';
import { HOSTED_REDIRECT_ERROR, useHostedRedirect } from './hostedRedirect';
import { formatCents, formatUtcDate } from './money';
import { PlanStartPanel } from './PlanStartPanel';
import { startOptionsFor } from './startOptions';
import { useResource } from './useResource';

/**
 * The plan actions for the state the server reported:
 *  - no plan / trial / trial ended / canceled: choose a plan, then Start free trial or Subscribe (or Reactivate);
 *  - active / pending change / payment failed in grace: change plan (with the exact charge shown BEFORE confirming),
 *    cancel, or undo a scheduled cancel or downgrade;
 *  - payment failed and restricted: plan changes are paused until the payment is collected.
 * Which of these appear mirrors `effectiveState`; the server still refuses anything it does not allow.
 */
export function PlanSection({
  subscription, plans, initialChangePlanCode,
}: {
  subscription: CompanySubscriptionResponse | null;
  plans: PlanSummary[];
  initialChangePlanCode: string | null;
}) {
  const { readOnly, providerAvailable, markReadOnly } = useBilling();
  const options = startOptionsFor(subscription);
  const state = subscription?.effectiveState ?? 'NoSubscription';
  const [chosenCode, setChosenCode] = useState<string | null>(initialChangePlanCode);
  const [notice, setNotice] = useState<string | null>(null);

  if (readOnly) {
    return (
      <section aria-labelledby="billing-change-heading" className="billing-section">
        <h3 id="billing-change-heading">Plan</h3>
        <p className="muted">Plan changes, cancellation and subscribing are available to users with billing-management permission.</p>
      </section>
    );
  }

  if (options.fixPaymentFirst) {
    return (
      <section aria-labelledby="billing-change-heading" className="billing-section">
        <h3 id="billing-change-heading">Plan</h3>
        <p>Plan changes are paused until your payment is collected. Update your payment method above and the company returns to full access.</p>
      </section>
    );
  }

  if (state === 'Canceled') {
    return (
      <section aria-labelledby="billing-change-heading" className="billing-section">
        <h3 id="billing-change-heading">Reactivate</h3>
        <ReactivatePanel
          label="Reactivate subscription"
          description="You will go to the secure checkout to restart your subscription. You will see taxes before you pay."
        />
        <h4>Or choose a different plan</h4>
        <PlanChooser plans={plans} subscription={subscription} chosenCode={chosenCode} onChoose={setChosenCode} />
      </section>
    );
  }

  if (options.changePlan) {
    const hasScheduled = subscription?.cancelAtPeriodEnd || subscription?.pendingPlanCode;
    return (
      <section aria-labelledby="billing-change-heading" className="billing-section">
        <h3 id="billing-change-heading">Change plan or cancel</h3>
        {notice && <p className="success" role="status" data-testid="plan-change-result">{notice}</p>}

        {hasScheduled && (
          <ReactivatePanel
            label={subscription?.cancelAtPeriodEnd ? 'Keep my subscription' : 'Remove the scheduled change'}
            description={subscription?.cancelAtPeriodEnd
              ? 'Your subscription is set to cancel at the end of the billing period. You can undo that now.'
              : 'A plan change is scheduled for the end of the billing period. You can remove it now.'}
          />
        )}

        <ChangePlanPanel
          plans={plans}
          subscription={subscription!}
          chosenCode={chosenCode}
          onChoose={(code) => { setNotice(null); setChosenCode(code); }}
          onDone={(message) => { setNotice(message); setChosenCode(null); }}
          providerAvailable={providerAvailable}
          onReadOnly={markReadOnly}
        />

        {!subscription?.cancelAtPeriodEnd && <CancelPanel subscription={subscription!} onDone={(message) => setNotice(message)} />}
      </section>
    );
  }

  // No plan yet, trial not started, trial active, or trial ended.
  return (
    <section aria-labelledby="billing-change-heading" className="billing-section">
      <h3 id="billing-change-heading">{options.trial ? 'Choose a plan' : 'Subscribe to a plan'}</h3>
      <PlanChooser plans={plans} subscription={subscription} chosenCode={chosenCode} onChoose={setChosenCode} />
    </section>
  );
}

function PlanChooser({
  plans, subscription, chosenCode, onChoose,
}: {
  plans: PlanSummary[];
  subscription: CompanySubscriptionResponse | null;
  chosenCode: string | null;
  onChoose: (code: string) => void;
}) {
  const { providerAvailable, markReadOnly } = useBilling();
  const chosen = plans.find((p) => p.code === chosenCode) ?? null;
  return (
    <div>
      <ul className="billing-plan-list">
        {plans.map((plan) => (
          <li key={plan.id}>
            <div>
              <strong>{plan.name}</strong>
              <span className="muted"> {formatCents(plan.baseMonthlyFeeCents)}/mo + {formatCents(plan.perEmployeeMonthlyFeeCents)} per billable employee</span>
            </div>
            <button type="button" aria-pressed={chosenCode === plan.code} aria-label={`Choose ${plan.name}`} onClick={() => onChoose(plan.code)}>
              {chosenCode === plan.code ? 'Selected' : 'Choose'}
            </button>
          </li>
        ))}
      </ul>
      {chosen && (
        <PlanStartPanel plan={chosen} subscription={subscription} providerAvailable={providerAvailable} onReadOnly={markReadOnly} />
      )}
    </div>
  );
}

/** Undo a scheduled cancel or downgrade, or — for a canceled company — restart through checkout. */
function ReactivatePanel({ label, description }: { label: string; description: string }) {
  const { refresh, markReadOnly } = useBilling();
  const redirect = useHostedRedirect();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keyRef = useRef<string | null>(null);

  async function reactivate() {
    setBusy(true);
    setError(null);
    keyRef.current ??= crypto.randomUUID();
    try {
      const result = await subscriptionGateway.reactivateSubscription({ idempotencyKey: keyRef.current });
      keyRef.current = null;
      if (result.checkout) {
        try {
          redirect(result.checkout.url);
        } catch {
          setError(HOSTED_REDIRECT_ERROR);
        }
      } else {
        notifySubscriptionChanged();
        refresh();
      }
    } catch (err) {
      if (err instanceof ApiError && err.status < 500) keyRef.current = null;
      if (isPermissionDenied(err)) markReadOnly();
      else setError(billingErrorMessage(err, 'Could not reactivate the subscription. Nothing was changed.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="billing-action">
      <p>{description}</p>
      {error && <p className="error" role="alert">{error}</p>}
      <button type="button" disabled={busy} onClick={() => void reactivate()}>{busy ? 'Working…' : label}</button>
    </div>
  );
}

function ChangePlanPanel({
  plans, subscription, chosenCode, onChoose, onDone, providerAvailable, onReadOnly,
}: {
  plans: PlanSummary[];
  subscription: CompanySubscriptionResponse;
  chosenCode: string | null;
  onChoose: (code: string) => void;
  onDone: (message: string) => void;
  providerAvailable: boolean;
  onReadOnly: () => void;
}) {
  const chosen = plans.find((p) => p.code === chosenCode && p.code !== subscription.planCode) ?? null;
  return (
    <div>
      <h4>Change plan</h4>
      <p className="muted">
        Upgrades start immediately and you see the exact charge first. Downgrades take effect at the end of the current billing period.
      </p>
      <ul className="billing-plan-list">
        {plans.map((plan) => {
          const current = plan.code === subscription.planCode;
          return (
            <li key={plan.id}>
              <div>
                <strong>{plan.name}</strong>{current && <span className="badge badge--approved"> Current plan</span>}
                <span className="muted"> {formatCents(plan.baseMonthlyFeeCents)}/mo + {formatCents(plan.perEmployeeMonthlyFeeCents)} per billable employee</span>
              </div>
              {!current && (
                <button
                  type="button"
                  aria-pressed={chosenCode === plan.code}
                  aria-label={`Review change to ${plan.name}`}
                  disabled={!providerAvailable}
                  onClick={() => onChoose(plan.code)}
                >
                  Review change
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {chosen && (
        <PlanChangePreviewPanel
          key={chosen.code}
          plan={chosen}
          currentPlanName={subscription.planName}
          onDone={onDone}
          onReadOnly={onReadOnly}
        />
      )}
    </div>
  );
}

/** Shows the server's own preview of a plan change BEFORE anything can be confirmed: the exact amount due now, tax on
 * its own line, and the date it takes effect. The confirm button does not exist until a preview has loaded, and it
 * sends the amount the user saw so the server can refuse (409 PREVIEW_CHANGED) if it moved. */
function PlanChangePreviewPanel({
  plan, currentPlanName, onDone, onReadOnly,
}: {
  plan: PlanSummary;
  currentPlanName: string;
  onDone: (message: string) => void;
  onReadOnly: () => void;
}) {
  const { refresh } = useBilling();
  const [nonce, setNonce] = useState(0);
  const load = useCallback((signal: AbortSignal) => subscriptionGateway.getPlanChangePreview(plan.code, signal), [plan.code, nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const { state, reload } = useResource(load, 'Could not load the price for this change.', { keepPrevious: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keyRef = useRef<string | null>(null);

  useEffect(() => {
    if (state.status === 'denied') onReadOnly();
  }, [state.status, onReadOnly]);

  if (state.status === 'loading') return <p className="muted" role="status">Loading the exact amount…</p>;
  if (state.status === 'denied') return null;
  if (state.status === 'unavailable') return <p className="billing-notice billing-notice--warning" role="status">Billing is not available in this environment. Nothing was changed.</p>;
  if (state.status === 'error') return <RetryableError message={state.message} onRetry={reload} />;

  const preview: PlanChangePreview = state.data;

  async function confirm() {
    setBusy(true);
    setError(null);
    keyRef.current ??= crypto.randomUUID();
    try {
      await subscriptionGateway.changePlan({
        planCode: plan.code, idempotencyKey: keyRef.current, expectedAmountDueNowCents: preview.amountDueNowCents,
      });
      keyRef.current = null;
      notifySubscriptionChanged();
      refresh();
      onDone(preview.kind === 'Downgrade'
        ? `Your change to ${plan.name} is scheduled. It takes effect at the end of the current billing period, on ${formatUtcDate(preview.effectiveAtUtc)}. You keep ${currentPlanName} until then.`
        : `You are now on ${plan.name}. ${formatCents(preview.amountDueNowCents)} was charged.`);
    } catch (err) {
      if (err instanceof ApiError && err.status < 500) keyRef.current = null;
      if (isPermissionDenied(err)) {
        onReadOnly();
      } else if (err instanceof ApiError && err.code === 'PREVIEW_CHANGED') {
        setError(billingErrorMessage(err, ''));
        setNonce((n) => n + 1); // fetch the new amount; the user must review it and confirm again
      } else {
        setError(billingErrorMessage(err, 'Could not change the plan. Nothing was changed.'));
      }
    } finally {
      setBusy(false);
    }
  }

  const chargeLines = preview.lines.filter((line) => line.kind !== 'Tax');

  return (
    <section className="plan-preview" aria-label={`Review your change to ${plan.name}`} data-testid="plan-change-preview">
      <h5>Review your change to {plan.name}</h5>

      {preview.kind === 'Same' && <p>{plan.name} is already your plan. There is nothing to change.</p>}

      {preview.kind === 'Upgrade' && (
        <p>This upgrade takes effect today, {formatUtcDate(preview.effectiveAtUtc)}. You will be charged the amount below now.</p>
      )}

      {preview.kind === 'Downgrade' && (
        <p data-testid="downgrade-copy">
          This change takes effect at the end of the current billing period, on {formatUtcDate(preview.effectiveAtUtc)}. You
          keep {currentPlanName} until then, and nothing is charged now.
        </p>
      )}

      {preview.kind !== 'Same' && <PlanChangeConsequences preview={preview} plan={plan} />}

      {preview.kind !== 'Same' && (
        <table className="billing-table" aria-label="Charges for this change">
          <tbody>
            {chargeLines.map((line, index) => (
              <tr key={`${line.description}-${index}`}>
                <th scope="row">{line.description}</th>
                <td className="billing-table__amount">{formatCents(line.amountCents)}</td>
              </tr>
            ))}
            <tr data-testid="preview-tax">
              <th scope="row">Tax</th>
              <td className="billing-table__amount">{formatCents(preview.taxCents)}</td>
            </tr>
            <tr className="billing-table__total" data-testid="preview-total">
              <th scope="row">Amount due now</th>
              <td className="billing-table__amount">{formatCents(preview.amountDueNowCents)}</td>
            </tr>
          </tbody>
        </table>
      )}

      {error && <p className="error" role="alert">{error}</p>}

      {preview.kind !== 'Same' && (
        <button type="button" disabled={busy} onClick={() => void confirm()}>
          {busy
            ? 'Confirming…'
            : preview.kind === 'Upgrade'
              ? `Confirm upgrade and pay ${formatCents(preview.amountDueNowCents)}`
              : 'Confirm downgrade at the end of the billing period'}
        </button>
      )}
    </section>
  );
}

/** What the change does to features, exactly as the server's preview listed it: a downgrade lists what becomes read-only (with the
 * server's note that nothing is deleted), an upgrade lists what the company gains. An older server that sends neither shows nothing here. */
function PlanChangeConsequences({ preview, plan }: { preview: PlanChangePreview; plan: PlanSummary }) {
  const affected: PlanChangeFeatureImpact[] = preview.featuresAffected ?? [];
  const readOnly = affected.filter((f) => f.consequence === 'ReadOnly');
  const gained = affected.filter((f) => f.consequence === 'Gained');

  if (preview.kind === 'Downgrade' && (readOnly.length > 0 || preview.consequenceNote)) {
    return (
      <div className="plan-consequences" data-testid="downgrade-consequences">
        <h6>What changes for your features</h6>
        {readOnly.length > 0 && (
          <>
            <p><strong>Becomes read-only:</strong></p>
            <ul aria-label="Features that become read-only">
              {readOnly.map((f) => (
                <li key={f.featureKey}>
                  {f.displayName} <FeatureAvailabilityBadge readinessStatus={f.readinessStatus} />
                  <span className="muted"> (included from {f.requiredPlanName})</span>
                </li>
              ))}
            </ul>
          </>
        )}
        {preview.consequenceNote && <p data-testid="consequence-note">{preview.consequenceNote}</p>}
      </div>
    );
  }

  if (preview.kind === 'Upgrade' && gained.length > 0) {
    return (
      <div className="plan-consequences" data-testid="upgrade-gains">
        <h6>What you gain with {plan.name}</h6>
        <ul aria-label="Features you gain">
          {gained.map((f) => (
            <li key={f.featureKey}>
              {f.displayName} <FeatureAvailabilityBadge readinessStatus={f.readinessStatus} />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return null;
}

function CancelPanel({ subscription, onDone }: { subscription: CompanySubscriptionResponse; onDone: (message: string) => void }) {
  const { refresh, markReadOnly } = useBilling();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keyRef = useRef<string | null>(null);

  async function cancel() {
    setBusy(true);
    setError(null);
    keyRef.current ??= crypto.randomUUID();
    try {
      await subscriptionGateway.cancelSubscription({ idempotencyKey: keyRef.current });
      keyRef.current = null;
      notifySubscriptionChanged();
      refresh();
      setConfirming(false);
      onDone(`Your cancellation is scheduled. It takes effect at the end of the current billing period, on ${formatUtcDate(subscription.currentPeriodEndUtc)}. You keep full access until then.`);
    } catch (err) {
      if (err instanceof ApiError && err.status < 500) keyRef.current = null;
      if (isPermissionDenied(err)) markReadOnly();
      else setError(billingErrorMessage(err, 'Could not cancel the subscription. Nothing was changed.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="billing-action billing-action--danger">
      <h4>Cancel subscription</h4>
      {!confirming && (
        <button type="button" className="billing-button--secondary" onClick={() => setConfirming(true)}>Cancel subscription</button>
      )}
      {confirming && (
        <div role="group" aria-label="Confirm cancellation" data-testid="cancel-confirm">
          <p>
            Canceling takes effect at the end of the current billing period, on {formatUtcDate(subscription.currentPeriodEndUtc)}.
            You keep full access until then. Fees already paid are generally non-refundable. Your payroll records are never deleted.
          </p>
          {error && <p className="error" role="alert">{error}</p>}
          <div className="inline-form">
            <button type="button" className="billing-button--danger" disabled={busy} onClick={() => void cancel()}>
              {busy ? 'Canceling…' : 'Confirm cancellation at the end of the period'}
            </button>
            <button type="button" className="link" onClick={() => setConfirming(false)}>Keep my subscription</button>
          </div>
        </div>
      )}
    </div>
  );
}
